import React from 'react';
import Header from '../Header';

const RefundPolicy = () => (
  <>
    <Header />
    <div className='container-md'>
      <h1 className='h1 mb-4'>Refund and Cancellation Policy</h1>
      <p><strong>Last Updated: December 31, 2025</strong></p>

      <h2 className='h2 mt-4 mb-3'>Introduction</h2>
      <p>
        This Refund and Cancellation Policy applies to event registrations (such as marathon
        participation) and other paid services offered by Rupee For Humanity. Please read this
        policy carefully before making any payment.
      </p>

      <h2 className='h2 mt-4 mb-3'>Donations</h2>
      <p>
        <strong>All donations made to Rupee For Humanity are final and non-refundable.</strong> As a
        registered charitable organization under Section 80G of the Income Tax Act, 1961, donations
        are considered voluntary contributions to support our charitable activities. Once a donation
        receipt (80G certificate) is issued, the donation cannot be refunded.
      </p>
      <p>
        If you have made a donation in error, please contact us immediately at{' '}
        <a href="mailto:rupee4humanity@gmail.com">rupee4humanity@gmail.com</a>. We will review your
        request on a case-by-case basis.
      </p>

      <h2 className='h2 mt-4 mb-3'>Event Registration Refunds</h2>

      <h3 className='h3 mt-3'>When Refunds Are Provided</h3>
      <p>
        Refunds for event registrations (such as marathon participation fees) will only be provided
        in the following circumstances:
      </p>
      <ul>
        <li>
          <strong>Event Cancellation by Rupee For Humanity:</strong> If we cancel an event due to
          circumstances beyond our control (natural disasters, pandemics, government restrictions,
          war, etc.), a full refund will be issued to all registered participants.
        </li>
        <li>
          <strong>Duplicate Payment:</strong> If you accidentally make multiple payments for the
          same registration, we will refund the duplicate payment(s).
        </li>
        <li>
          <strong>Technical Error:</strong> If a payment was processed due to a technical error on
          our Website, we will provide a full refund.
        </li>
      </ul>

      <h3 className='h3 mt-3'>When Refunds Are NOT Provided</h3>
      <p>Refunds will <strong>NOT</strong> be issued in the following situations:</p>
      <ul>
        <li>Participant cancellation after registration deadline</li>
        <li>Participant no-show on event day</li>
        <li>Personal reasons (illness, travel issues, change of plans, etc.)</li>
        <li>Weather conditions (unless the event is officially cancelled by Rupee For Humanity)</li>
        <li>Participant disqualification due to violation of event rules or code of conduct</li>
      </ul>

      <h2 className='h2 mt-4 mb-3'>Cancellation Policy</h2>

      <h3 className='h3 mt-3'>Participant-Initiated Cancellations</h3>
      <p>
        If you wish to cancel your event registration, please contact us at{' '}
        <a href="mailto:rupee4humanity@gmail.com">rupee4humanity@gmail.com</a> with your registration
        details.
      </p>
      <ul>
        <li>
          <strong>Cancellations before event start:</strong> Requests must be submitted before the
          event begins. Cancellations made after the event starts will not be accepted.
        </li>
        <li>
          <strong>No refund for participant cancellations:</strong> Even if you cancel before the
          event, registration fees are generally non-refundable as they contribute to event
          organization costs and our charitable mission.
        </li>
      </ul>

      <h3 className='h3 mt-3'>Organization-Initiated Cancellations</h3>
      <p>
        Rupee For Humanity reserves the right to cancel any registration or refuse service without
        providing a reason, including but not limited to:
      </p>
      <ul>
        <li>Inability to pay fees</li>
        <li>Unacceptable behavior or violation of our code of conduct</li>
        <li>Failure to comply with event terms and conditions</li>
        <li>Providing false or misleading information during registration</li>
        <li>Any action that may harm other participants, volunteers, or our organization's reputation</li>
      </ul>
      <p>
        In cases where we cancel your registration due to our own reasons (not participant misconduct),
        a full refund will be provided.
      </p>

      <h2 className='h2 mt-4 mb-3'>Refund Process and Timeline</h2>

      <h3 className='h3 mt-3'>How to Request a Refund</h3>
      <p>To request a refund (if eligible), please contact us with the following information:</p>
      <ul>
        <li>Your full name</li>
        <li>Email address used for registration</li>
        <li>Transaction ID or receipt number</li>
        <li>Reason for refund request</li>
        <li>Proof of payment (screenshot or receipt)</li>
      </ul>
      <p>
        Email your refund request to: <a href="mailto:rupee4humanity@gmail.com">rupee4humanity@gmail.com</a>
      </p>

      <h3 className='h3 mt-3'>Refund Timeline</h3>
      <p>
        If your refund request is approved, the refund will be processed within <strong>30 days</strong>{' '}
        of approval. The timeline accounts for the following:
      </p>
      <ul>
        <li>Verification of eligibility (5-7 business days)</li>
        <li>Accounting reconciliation (funds raised support children's education and charitable activities)</li>
        <li>Payment processing by financial institutions (7-14 business days)</li>
      </ul>
      <p>
        Refunds will be credited to the original payment method used during registration. If the
        original payment method is no longer valid, please inform us of an alternative refund method.
      </p>

      <h2 className='h2 mt-4 mb-3'>Eligibility Criteria</h2>
      <p>To be eligible for a refund, you must meet the following criteria:</p>
      <ul>
        <li>Your refund request falls under one of the eligible categories mentioned above</li>
        <li>You have provided all required documentation and information</li>
        <li>The refund request is submitted within a reasonable timeframe (preferably within 7 days of the event date or payment)</li>
        <li>There is no evidence of fraudulent activity or terms violation</li>
      </ul>

      <h2 className='h2 mt-4 mb-3'>Partial Refunds</h2>
      <p>
        In some cases, we may offer partial refunds at our discretion. For example:
      </p>
      <ul>
        <li>If an event is significantly shortened or modified, we may offer a prorated refund</li>
        <li>If certain promised amenities or services are unavailable, we may provide a partial refund</li>
      </ul>
      <p>
        Partial refund amounts will be determined on a case-by-case basis by the Rupee For Humanity management team.
      </p>

      <h2 className='h2 mt-4 mb-3'>Transfer of Registration</h2>
      <p>
        <strong>Alternative to Cancellation:</strong> Instead of requesting a refund, you may transfer
        your registration to another person, subject to the following conditions:
      </p>
      <ul>
        <li>Transfer requests must be submitted at least 48 hours before the event</li>
        <li>The new participant must meet all event eligibility requirements</li>
        <li>A transfer fee may apply (if applicable, will be communicated at the time of registration)</li>
        <li>Transfer requests must be submitted via email to: <a href="mailto:rupee4humanity@gmail.com">rupee4humanity@gmail.com</a></li>
      </ul>

      <h2 className='h2 mt-4 mb-3'>Deferral to Future Events</h2>
      <p>
        In certain circumstances, we may allow you to defer your registration to a future event:
      </p>
      <ul>
        <li>Medical emergencies (requires documentation)</li>
        <li>Bereavement or family emergencies</li>
        <li>Natural disasters or government travel restrictions</li>
      </ul>
      <p>
        Deferral requests are subject to approval and must be submitted before the event date. Contact us at{' '}
        <a href="mailto:rupee4humanity@gmail.com">rupee4humanity@gmail.com</a> to discuss deferral options.
      </p>

      <h2 className='h2 mt-4 mb-3'>Contact Us</h2>
      <p>
        If you have any questions about our Refund and Cancellation Policy, please contact us:
      </p>
      <ul>
        <li><strong>Email:</strong> <a href="mailto:rupee4humanity@gmail.com">rupee4humanity@gmail.com</a></li>
        <li><strong>Website:</strong> <a href="https://www.rupeeforhumanity.org/">https://www.rupeeforhumanity.org/</a></li>
      </ul>
      <p>We will respond to your inquiry within 7 business days.</p>

      <h2 className='h2 mt-4 mb-3'>Changes to This Policy</h2>
      <p>
        We reserve the right to modify this Refund and Cancellation Policy at any time. Any changes
        will be posted on this page with an updated "Last Updated" date. Your continued participation
        in our events after changes are posted constitutes acceptance of the updated policy.
      </p>

      <hr className="my-5" />

      <p className="text-muted small">
        <strong>Note:</strong> This policy applies to event registrations and paid services. For
        questions about donation receipts and 80G certificates, please refer to our Privacy Policy
        and contact our team directly.
      </p>
    </div>
  </>
);

export default RefundPolicy;
